
import { Info } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/Common/Card';
import { SectorTemplate } from './types';

interface InformationTabProps {
  templates: SectorTemplate[];
}

const UNDERSTAND_ITEMS = ['item1', 'item2', 'item3', 'item4'] as const;
const USEFUL_ITEMS = ['item1', 'item2', 'item3', 'item4'] as const;
const SUMMARY_ITEMS = ['item1', 'item2', 'item3', 'item4', 'item5'] as const;

export function InformationTab({ templates }: InformationTabProps) {
  const { t } = useTranslation();

  const withoutLabel = (
    <span className="font-semibold text-red-600 dark:text-red-400">
      {t('knowledgeManagement.information.impact.withoutLabel')}
    </span>
  );
  const withLabel = (
    <span className="font-semibold text-green-600 dark:text-green-400">
      {t('knowledgeManagement.information.impact.withLabel')}
    </span>
  );

  return (
    <Card className="border-none shadow-none bg-transparent">
      <CardHeader className="px-0 pt-0">
        <CardDescription>{t('knowledgeManagement.information.intro')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 px-0 pb-0">
        <section className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
          <h2 className="text-lg font-semibold text-amber-900 dark:text-amber-100 mb-3 flex items-center gap-2">
            <Info className="h-5 w-5" />
            {t('knowledgeManagement.information.principleTitle')}
          </h2>
          <p className="text-sm leading-relaxed text-amber-900 dark:text-amber-100">
            {t('knowledgeManagement.information.principleText')}
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-foreground mb-3">
            {t('knowledgeManagement.information.benefitsTitle')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
              <h3 className="text-base font-semibold text-green-900 dark:text-green-100 mb-2">
                {t('knowledgeManagement.information.understand.title')}
              </h3>
              <ul className="text-sm leading-relaxed text-green-900 dark:text-green-100 space-y-1">
                {UNDERSTAND_ITEMS.map((item) => (
                  <li key={item}>• {t(`knowledgeManagement.information.understand.${item}`)}</li>
                ))}
              </ul>
            </div>
            <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
              <h3 className="text-base font-semibold text-blue-900 dark:text-blue-100 mb-2">
                {t('knowledgeManagement.information.useful.title')}
              </h3>
              <ul className="text-sm leading-relaxed text-blue-900 dark:text-blue-100 space-y-1">
                {USEFUL_ITEMS.map((item) => (
                  <li key={item}>• {t(`knowledgeManagement.information.useful.${item}`)}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-foreground mb-3">
            {t('knowledgeManagement.information.impact.title')}
          </h2>
          <div className="bg-muted/30 rounded-lg p-4 space-y-4 border border-border">
            <div className="space-y-2">
              <p className="text-sm leading-relaxed text-foreground">
                {withoutLabel} {t('knowledgeManagement.information.impact.without1')}
              </p>
              <p className="text-sm leading-relaxed text-foreground">
                {withLabel} {t('knowledgeManagement.information.impact.with1')}
              </p>
            </div>
            <div className="border-t border-border pt-3 space-y-2">
              <p className="text-sm leading-relaxed text-foreground">
                {withoutLabel} {t('knowledgeManagement.information.impact.without2')}
              </p>
              <p className="text-sm leading-relaxed text-foreground">
                {withLabel} {t('knowledgeManagement.information.impact.with2')}
              </p>
            </div>
          </div>
        </section>

        <section className="bg-purple-50 dark:bg-purple-950/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
          <h2 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-3">
            {t('knowledgeManagement.information.summary.title')}
          </h2>
          <div className="space-y-2 text-sm leading-relaxed text-purple-900 dark:text-purple-100">
            {SUMMARY_ITEMS.map((item) => (
              <p key={item}>
                <Trans
                  i18nKey={`knowledgeManagement.information.summary.${item}`}
                  components={{ strong: <strong /> }}
                />
              </p>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-foreground mb-3">
            {t('knowledgeManagement.information.templates.title')}
          </h2>
          <p className="text-sm text-foreground/80 mb-4">
            {t('knowledgeManagement.information.templates.description')}
          </p>

          {templates.length === 0 ? (
            <div className="rounded-lg border border-border bg-muted/20 p-4">
              <p className="text-sm text-foreground/70">{t('knowledgeManagement.information.templates.empty')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map((template) => (
                <div key={template.id} className="rounded-lg border border-border bg-card p-4">
                  <h4 className="font-semibold text-foreground mb-1">{template.name}</h4>
                  <p className="text-sm text-foreground/80">{template.description}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {t('knowledgeManagement.information.templates.itemCount', { count: template.file_count })}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>
      </CardContent>
    </Card>
  );
}
